import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import {
  SHOPIFY_OAUTH_SHOP_KEY,
  SHOPIFY_OAUTH_STATE_KEY,
} from '@/features/integrations/shopifyOAuth';

/**
 * Shopify OAuth2 redirect handler. Shopify redirects here with
 * `code` + `shop` + `state` + `hmac` (and `host` + `timestamp`). We:
 *   1. Verify `state` against the value stashed in sessionStorage by
 *      `startShopifyOAuth` (CSRF protection — the server checks it again,
 *      but failing fast here saves a round-trip).
 *   2. Forward the *raw* querystring (Shopify's HMAC is computed over
 *      the original encoding, so we can't round-trip through
 *      URLSearchParams without breaking it) to `shopify-oauth-callback`,
 *      which verifies the HMAC, exchanges the code for an offline
 *      access token, and provisions the sales_channels row.
 *   3. Show a success toast and navigate back to /integrations. The
 *      Edge Function fires an initial `shopify-sync` itself, so the
 *      OrdersPage lights up by the time the user gets back.
 */
export function ShopifyCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode mounts effects twice in dev; the OAuth `code` is
  // single-use and Shopify rejects the second exchange.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const shop = params.get('shop');
    const state = params.get('state');
    const oauthError = params.get('error') ?? params.get('error_description');

    if (oauthError) {
      setError(`Shopify rejected the install: ${oauthError}`);
      return;
    }
    if (!code || !shop || !state) {
      setError('Missing OAuth parameters from Shopify.');
      return;
    }

    const expectedState = sessionStorage.getItem(SHOPIFY_OAUTH_STATE_KEY);
    if (!expectedState || state !== expectedState) {
      setError(
        'OAuth state mismatch. For your security, retry the connection from /integrations.',
      );
      return;
    }
    const expectedShop = sessionStorage.getItem(SHOPIFY_OAUTH_SHOP_KEY);
    if (expectedShop && expectedShop.toLowerCase() !== shop.toLowerCase()) {
      setError(
        `OAuth shop mismatch: you started the install on ${expectedShop} but Shopify redirected back as ${shop}. Retry the connection.`,
      );
      return;
    }
    sessionStorage.removeItem(SHOPIFY_OAUTH_STATE_KEY);
    sessionStorage.removeItem(SHOPIFY_OAUTH_SHOP_KEY);

    // Strip the leading `?` so the server sees only the parameter pairs.
    // `window.location.search` preserves the exact encoding Shopify
    // signed — `URLSearchParams.toString()` would re-encode and break
    // the HMAC.
    const rawQuery = window.location.search.replace(/^\?/, '');

    (async () => {
      const { data, error: fnError } = await supabase.functions.invoke<{
        ok: boolean;
        shop_name?: string;
        error?: string;
      }>('shopify-oauth-callback', {
        body: { query: rawQuery, state },
      });
      if (fnError) {
        const msg = await extractFunctionErrorMessage(fnError);
        setError(msg);
        return;
      }
      if (data && data.ok === false && data.error) {
        setError(data.error);
        return;
      }

      const shopName = data?.shop_name ?? shop;
      toast.success(`Connected ${shopName}. Pulling orders…`);
      navigate('/integrations', { replace: true });
    })();
  }, [params, navigate]);

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="space-y-3 text-center max-w-lg">
          <h1 className="text-xl font-medium tracking-headline">Connection failed</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/integrations', { replace: true })}
            className="text-sm text-primary underline-offset-2 hover:underline"
          >
            Back to Integrations
          </button>
        </div>
      </div>
    );
  }
  return <LoadingScreen />;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
